import c from 'classnames';
import { getPictureUrl } from 'core/api/pictures';
import Breadcrumbs from 'core/components/common/Breadcrumbs';
import { useLinks } from 'core/components/linksContext';
import { useSettings } from 'core/components/settingsContext';
import { column, row, title } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { isMobile } from 'core/utils';
import React, { useEffect } from 'react';
import PayButtonContainer from 'site/components/payments/PayButtonContainer';
import { IPayProductForm } from 'site/components/payments/PayButtonPresent';
import { Product } from 'site/graphql';
import ProductFeatures, { IProductFeaturesProduct, IProductFeaturesProductForm } from './ProductFeatures';
import { trackDetail } from '../../../../core/tracking';

type IProductHeaderSectionProductForm = IPayProductForm & IProductFeaturesProductForm;
type IProductHeaderSectionProduct = IProductFeaturesProduct &
    Pick<Product, 'name' | 'shortDescription' | 'pictureId' | 'tagsString' | 'contentProvider'>;

type IProductHeaderSectionProps = {
    product: IProductHeaderSectionProduct;
    breadcrumbsContrast: boolean;
    selectedProductForm?: IProductHeaderSectionProductForm;
} & WithStyles<typeof styles>;

const ProductHeaderSection: React.FC<IProductHeaderSectionProps> = ({
    classes,
    breadcrumbsContrast,
    product,
    selectedProductForm,
}) => {
    const links = useLinks();
    const settings = useSettings();

    useEffect(() => {
        if (selectedProductForm) {
            trackDetail({
                id: product.id,
                name: product.name,
                price: selectedProductForm.price,
                variant: selectedProductForm.type,
                category: product.tagsString,
                brand: product.contentProvider.name,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProductForm?.id]);

    return (
        <div className={classes.container}>
            <div className={classes.breadcrumbs}>
                <Breadcrumbs
                    contrast={breadcrumbsContrast}
                    items={[
                        { href: links.home && links.home.url(), title: 'Главная' },
                        { href: links.courseList.url(), title: 'Курсы' },
                        { title: product.name },
                    ]}
                />
            </div>

            <div className={classes.main}>
                <h1 className={classes.title}>{product.name}</h1>
                <p className={classes.shortDesc}>{product.shortDescription}</p>
                {selectedProductForm && (
                    <div className={c(classes.payButton, classes.payButtonMobile)}>
                        <PayButtonContainer productForm={selectedProductForm} />
                    </div>
                )}
                {selectedProductForm && (
                    <ProductFeatures
                        mode="main"
                        productForm={selectedProductForm}
                        product={product}
                        features={
                            isMobile()
                                ? ['start', 'price', 'type', 'complexity', 'lecturers', 'quantity']
                                : ['type', 'complexity', 'lecturers', 'quantity']
                        }
                    />
                )}
            </div>

            <div className={classes.sidebar}>
                <div
                    className={classes.image}
                    style={{
                        background: `url("${getPictureUrl(
                            product.pictureId,
                            '370x180',
                            settings,
                        )}") center center / cover no-repeat`,
                    }}
                />
                {selectedProductForm && (
                    <ProductFeatures
                        mode="side"
                        productForm={selectedProductForm}
                        product={product}
                        features={['start', 'price']}
                    />
                )}
                {selectedProductForm && (
                    <div className={classes.payButton}>
                        <PayButtonContainer productForm={selectedProductForm} />
                    </div>
                )}
            </div>
        </div>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        container: {
            ...row(),
        },
        breadcrumbs: {
            ...column(),
        },
        main: {
            ...column(),
            paddingBottom: 1.5 + 'rem',

            [theme.breakpoints.up('sm')]: {
                ...column({ cols: 8 }),
            },

            [theme.breakpoints.down('xs')]: {
                paddingBottom: 0.75 + 'rem',
            },
        },
        sidebar: {
            ...column(),
            display: 'none',
            marginBottom: 2 + 'rem',

            [theme.breakpoints.up('sm')]: {
                display: 'block',
                ...column({ cols: 4 }),
            },
        },
        title: {
            ...title({ size: 'h1', color: 'white' }),
            marginBottom: '1.5rem',
            maxWidth: 80 + '%',

            [theme.breakpoints.down('sm')]: {
                fontSize: 1.25 + 'rem',
                lineHeight: 2 + 'rem',
                marginBottom: 1 + 'rem',
                maxWidth: 'unset',
            },
        },
        shortDesc: {
            color: '#CCCCCC',
            maxWidth: 80 + '%',

            [theme.breakpoints.down('sm')]: {
                marginBottom: 1 + 'rem',
                maxWidth: 'unset',
            },
        },
        image: {
            height: 174,
            width: 'auto',
            marginBottom: '0.75rem',
        },
        payButton: {
            '& > div': {
                flexDirection: 'row',
                marginLeft: -10,
                marginRight: -10,

                [theme.breakpoints.down('sm')]: {
                    justifyContent: 'center',
                },
            },
            '& div > *': {
                marginLeft: 10,
                marginRight: 10,
            },
            '& * :last-child': {
                marginTop: 0,
            },
        },
        payButtonMobile: {
            display: 'none',
            marginBottom: 20,

            [theme.breakpoints.down('xs')]: {
                display: 'block',
            },
        },
    });

export default injectSheet(styles, 'ProductHeaderSection')(ProductHeaderSection);
